import React, { useState, useRef, useEffect, useCallback } from 'react';
import Webcam from 'react-webcam';

// 1. DEFINIZIONE DEI TIPI
// Importiamo il tipo 'Webcam' per usarlo con useRef
type ResolutionOption = {
  label: string;
  width: number;
  height: number;
};

// Funzione helper per calcolare l'aspect ratio
const getAspectRatio = (width: number, height: number): string => {
  const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
  const commonDivisor = gcd(width, height);
  return `${width / commonDivisor}:${height / commonDivisor}`;
};


// 2. DEFINIZIONE DEL COMPONENTE CON React.FC (Functional Component)
const WebcamComponent: React.FC = () => {
  // 3. TIPizzazione del REF
  const webcamRef = useRef<Webcam>(null);
  
  // 4. TIPizzazione degli STATI (useState)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [activeDeviceId, setActiveDeviceId] = useState<string | undefined>(undefined);
  const [cameraCapabilities, setCameraCapabilities] = useState<MediaTrackCapabilities | null>(null);
  const [resolutionOptions, setResolutionOptions] = useState<ResolutionOption[]>([]);
  const [selectedResolution, setSelectedResolution] = useState<ResolutionOption | null>(null);

  // 5. TIPizzazione dei PARAMETRI delle CALLBACK
  const handleDevices = useCallback((mediaDevices: MediaDeviceInfo[]) => {
    const videoDevices = mediaDevices.filter(({ kind }) => kind === "videoinput");
    setDevices(videoDevices);
    if (videoDevices.length > 0 && !activeDeviceId) {
      setActiveDeviceId(videoDevices[0].deviceId);
    }
  }, [activeDeviceId]);
  
  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then(handleDevices);
  }, [handleDevices]);

  const onUserMedia = (stream: MediaStream) => {
    if (webcamRef.current && webcamRef.current.stream) {
      const track = webcamRef.current.stream.getVideoTracks()[0];
      const capabilities = track.getCapabilities();
      
      console.log("Capacità della fotocamera:", capabilities);
      setCameraCapabilities(capabilities);
      
      if (capabilities.width?.max && capabilities.height?.max) {
        const maxWidth = capabilities.width.max;
        const maxHeight = capabilities.height.max;
        const aspectRatio = maxWidth / maxHeight;

        const maxRes: ResolutionOption = {
          label: `Massima (${maxWidth}x${maxHeight})`,
          width: maxWidth,
          height: maxHeight,
        };

        const options: ResolutionOption[] = [maxRes];

        setResolutionOptions(options);
        setSelectedResolution(maxRes);
      }
    }
  };
  
  // 6. TIPizzazione degli EVENTI
  const handleResolutionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [width, height] = e.target.value.split('x').map(Number);
    const selected = resolutionOptions.find(r => r.width === width && r.height === height);
    if (selected) {
      setSelectedResolution(selected);
    }
  };
  
  // L'oggetto videoConstraints viene inferito correttamente da TypeScript
  const videoConstraints = {
    deviceId: activeDeviceId,
    width: selectedResolution ? selectedResolution.width : undefined,
    height: selectedResolution ? selectedResolution.height : undefined,
  };

  return (
    <div style={{ fontFamily: 'sans-serif', padding: '20px' }}>
      <h2 style={{ textAlign: 'center' }}>Test Fotocamera con React-Webcam (TypeScript)</h2>
      
      <div style={{ marginBottom: '20px', padding: '15px', background: '#f0f0f0', borderRadius: '8px' }}>
        <h3>Controlli</h3>
        {devices.length > 1 && (
          <div style={{ marginBottom: '10px' }}>
            <label htmlFor="camera-select">Seleziona Fotocamera: </label>
            <select id="camera-select" onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setActiveDeviceId(e.target.value)} value={activeDeviceId}>
              {devices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${device.deviceId}`}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {/* Usiamo un controllo per assicurarci che selectedResolution non sia null */}
        {resolutionOptions.length > 0 && selectedResolution && (
          <div>
            <label htmlFor="resolution-select">Seleziona Risoluzione: </label>
            <select id="resolution-select" onChange={handleResolutionChange} value={`${selectedResolution.width}x${selectedResolution.height}`}>
              {resolutionOptions.map((res) => (
                <option key={res.label} value={`${res.width}x${res.height}`}>
                  {res.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={{ textAlign: 'center' }}>
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={videoConstraints}
          onUserMedia={onUserMedia}
          onUserMediaError={(error: string | DOMException) => console.error("Errore Webcam:", error)}
          style={{ maxWidth: '100%', borderRadius: '8px', border: '2px solid #333' }}
        />
      </div>

      {cameraCapabilities && (
        <div style={{ marginTop: '20px', padding: '15px', background: '#e8f4f8', borderRadius: '8px' }}>
          <h3>Caratteristiche Hardware</h3>
          <ul>
            <li><strong>Dispositivo:</strong> {devices.find(d => d.deviceId === activeDeviceId)?.label || 'N/A'}</li>
            <li><strong>Risoluzione Massima Supportata:</strong> {cameraCapabilities.width?.max} x {cameraCapabilities.height?.max}</li>
            {/* Controllo per assicurarsi che i valori esistano prima di fare calcoli */}
            {cameraCapabilities.width?.max && cameraCapabilities.height?.max &&
              <li><strong>Aspect Ratio Nativo:</strong> {getAspectRatio(cameraCapabilities.width.max, cameraCapabilities.height.max)}</li>
            }
            <li><strong>Frame Rate Massimo:</strong> {cameraCapabilities.frameRate?.max} fps</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default WebcamComponent;